import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Moon, Sun, Shield, Menu, X, Search } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { SearchModal } from "@/components/SearchModal";

const CATEGORIES = ["All", "People to Know", "Blogs", "Podcasts", "Congressional Hearings", "Conspiracies"];

const TICKER = [
  "AARO HISTORICAL RECORD REPORT RELEASED",
  "CONGRESSIONAL UAP HEARING TESTIMONY ONGOING",
  "NAVY RELEASES DECLASSIFIED COCKPIT FOOTAGE",
  "GALILEO PROJECT DEPLOYS NEW SENSOR ARRAY",
  "WHISTLEBLOWER CLAIMS NHI CRAFT RECOVERY",
];

export const Header = ({ activeCategory, onCategory }) => {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dark, setDark] = useState(() => localStorage.getItem("dc_theme") === "dark");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("dc_theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const utc = now.toISOString().slice(0, 19).replace("T", " ");
  const onHome = location.pathname === "/";

  const handleCategory = (c) => {
    setMobileOpen(false);
    if (!onHome) {
      navigate("/");
      setTimeout(() => onCategory && onCategory(c), 50);
    } else {
      onCategory && onCategory(c);
    }
  };

  return (
    <header className="sticky top-0 z-40">
      {/* Ticker */}
      <div className="bg-masthead text-masthead-ink overflow-hidden border-b border-white/10">
        <div className="ticker-track py-2">
          {[...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="font-mono2 text-[0.65rem] tracking-[0.2em] px-6 flex items-center">
              <span className="text-signal mr-2">●</span> {t}
            </span>
          ))}
        </div>
      </div>

      {/* Masthead */}
      <div className="bg-masthead text-masthead-ink">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16">
          <div className="flex items-center justify-between py-4 border-b border-white/10">
            <div className="hidden md:block font-mono2 text-[0.65rem] tracking-[0.2em] text-masthead-ink/60 w-48">
              {utc} UTC
            </div>
            <Link to="/" data-testid="masthead-logo" className="text-center flex-1" onClick={() => onCategory && onCategory("All")}>
              <div className="overline-label text-signal mb-1">EST. 2026 — CLASSIFIED ARCHIVE</div>
              <h1 className="font-display font-black text-2xl sm:text-4xl lg:text-5xl tracking-tight leading-none">
                DISCLOSURE JOURNAL
              </h1>
              <div className="font-body italic text-masthead-ink/70 text-xs sm:text-sm mt-1.5 tracking-wide">
                They're here. Keep up.
              </div>
            </Link>
            <div className="hidden md:flex items-center justify-end gap-3 w-48">
              <button
                data-testid="search-trigger"
                onClick={() => setSearchOpen(true)}
                className="p-2 hover:text-signal transition-colors"
                aria-label="Search archive"
              >
                <Search size={18} />
              </button>
              <button
                data-testid="theme-toggle"
                onClick={() => setDark((d) => !d)}
                className="p-2 hover:text-signal transition-colors"
                aria-label="Toggle theme"
              >
                {dark ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button
                data-testid="cart-trigger"
                onClick={() => setOpen(true)}
                className="relative p-2 hover:text-signal transition-colors"
                aria-label="Open cart"
              >
                <ShoppingBag size={18} />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-signal text-white text-[0.6rem] font-mono2 w-4 h-4 flex items-center justify-center rounded-full">
                    {count}
                  </span>
                )}
              </button>
            </div>
            {/* mobile */}
            <div className="flex md:hidden items-center gap-2">
              <button data-testid="search-trigger-mobile" onClick={() => setSearchOpen(true)} className="p-2" aria-label="Search">
                <Search size={18} />
              </button>
              <button data-testid="cart-trigger-mobile" onClick={() => setOpen(true)} className="relative p-2">
                <ShoppingBag size={18} />
                {count > 0 && (
                  <span className="absolute top-0 right-0 bg-signal text-white text-[0.6rem] font-mono2 w-4 h-4 flex items-center justify-center rounded-full">
                    {count}
                  </span>
                )}
              </button>
              <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((o) => !o)} className="p-2">
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {/* Nav */}
          <nav className="hidden md:flex flex-wrap items-center justify-center gap-x-6 gap-y-2 py-3">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`nav-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => handleCategory(c)}
                className={`overline-label whitespace-nowrap transition-colors hover:text-signal ${
                  onHome && activeCategory === c ? "text-signal" : "text-masthead-ink/80"
                }`}
              >
                {c}
              </button>
            ))}
            <span className="w-px h-4 bg-white/20" />
            <Link
              to="/store"
              data-testid="nav-store"
              className={`overline-label whitespace-nowrap transition-colors hover:text-signal ${
                location.pathname === "/store" ? "text-signal" : "text-masthead-ink/80"
              }`}
            >
              The Store
            </Link>
            {user ? (
              <>
                <Link to="/admin" data-testid="nav-admin" className="overline-label text-cyan hover:text-signal flex items-center gap-1">
                  <Shield size={12} /> Editor
                </Link>
                <button data-testid="nav-logout" onClick={logout} className="overline-label text-masthead-ink/50 hover:text-signal">
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login" className="overline-label text-masthead-ink/50 hover:text-signal flex items-center gap-1">
                <Shield size={12} /> Admin
              </Link>
            )}
          </nav>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-white/10 px-4 py-4 flex flex-col gap-3">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`mobile-nav-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => handleCategory(c)}
                className="overline-label text-left text-masthead-ink/80 hover:text-signal"
              >
                {c}
              </button>
            ))}
            <Link to="/store" onClick={() => setMobileOpen(false)} className="overline-label text-masthead-ink/80">The Store</Link>
            {user ? (
              <>
                <Link to="/admin" onClick={() => setMobileOpen(false)} className="overline-label text-cyan">Editor Desk</Link>
                <button onClick={() => { logout(); setMobileOpen(false); }} className="overline-label text-left text-masthead-ink/50">Logout</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="overline-label text-masthead-ink/50">Admin Login</Link>
            )}
            <button onClick={() => setDark((d) => !d)} className="overline-label text-left text-masthead-ink/50">
              {dark ? "Light Mode" : "Dark Mode"}
            </button>
          </div>
        )}
      </div>
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};
